import {
  Assets,
  Container,
  FederatedPointerEvent,
  Graphics,
  Rectangle,
  Sprite,
  Text,
  Texture
} from 'pixi.js';
import bookErrorIconUrl from '@/assets/images/book-error.png';
import type { ImageLoadFailureKind } from '@/types/canvas-type';

const ERROR_BG = 0xf5f5f5;
const TITLE_COLOR = 0x4a3b60;
const NETWORK_TEXT_COLOR = 0x000000;
const BUTTON_BG = 0x6033e1;
const BUTTON_TEXT_COLOR = 0xffffff;
/** Figma 单页短边为 535px；以下比例保持不同阅读尺寸下的视觉关系。 */
const ICON_RATIO = 0.149;
const CONTENT_TOP_HEIGHT_RATIO = 0.414;
const TITLE_RATIO = 0.028;
const SUBTITLE_RATIO = 0.024;
const NETWORK_TEXT_RATIO = 0.028;
const GAP_ICON_TITLE_RATIO = 0.017;
const GAP_TITLE_SUB_RATIO = 0.015;
const GAP_SUBTITLE_BUTTON_RATIO = 0.039;
const GAP_ICON_NETWORK_TEXT_RATIO = 0.024;
const GAP_NETWORK_TEXT_BUTTON_RATIO = 0.05;
const EDIT_BUTTON_WIDTH_RATIO = 0.39;
const RETRY_BUTTON_WIDTH_RATIO = 0.228;
const BUTTON_HEIGHT_RATIO = 0.06;
const BUTTON_TEXT_RATIO = 0.023;
const BUTTON_RADIUS_HEIGHT_RATIO = 0.44;
const BUTTON_ICON_HEIGHT_RATIO = 0.375;
const BUTTON_TEXT_ICON_GAP_HEIGHT_RATIO = 0.1875;

export interface PageErrorContentOptions {
  kind: ImageLoadFailureKind;
  onRetry?: () => void;
  onEdit?: () => void;
  editLabel?: string;
}

let iconTexturePromise: Promise<Texture> | null = null;

/** 全书共享：仅首次失败时加载图标纹理。 */
function loadErrorIconTexture(): Promise<Texture> {
  if (!iconTexturePromise) {
    iconTexturePromise = Assets.load(bookErrorIconUrl).catch((error) => {
      console.error('失败态图标加载失败:', error);
      iconTexturePromise = null;
      return Texture.EMPTY;
    });
  }
  return iconTexturePromise;
}

/** 文字超采样分辨率，避免页面缩放后发糊。 */
function getErrorTextResolution(): number {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  return Math.max(dpr, 2) * 2;
}

/** 懒构建网络失败或永久失败页面内容。 */
export async function buildPageErrorContent(
  layer: Container,
  width: number,
  height: number,
  options: PageErrorContentOptions
): Promise<void> {
  if (layer.destroyed) return;

  const previousChildren = layer.removeChildren();
  previousChildren.forEach((child) => child.destroy({ children: true }));

  const shortSide = Math.min(width, height);
  const iconSize = Math.round(shortSide * ICON_RATIO);
  const buttonHeight = Math.round(shortSide * BUTTON_HEIGHT_RATIO);
  const buttonRadius = Math.round(buttonHeight * BUTTON_RADIUS_HEIGHT_RATIO);
  const buttonTextSize = Math.round(shortSide * BUTTON_TEXT_RATIO);
  const textResolution = getErrorTextResolution();
  const centerX = width / 2;
  const blockTop = height * CONTENT_TOP_HEIGHT_RATIO;

  const background = new Graphics();
  background.rect(0, 0, width, height).fill({ color: ERROR_BG, alpha: 1 });
  layer.addChild(background);

  const icon = new Sprite();
  icon.width = iconSize;
  icon.height = iconSize;
  icon.anchor.set(0.5, 0);
  icon.position.set(centerX, blockTop);
  layer.addChild(icon);

  if (options.kind === 'network') {
    buildNetworkFailureContent(layer, {
      centerX,
      shortSide,
      iconBottom: blockTop + iconSize,
      buttonHeight,
      buttonRadius,
      buttonTextSize,
      textResolution,
      onRetry: options.onRetry
    });
  } else {
    buildPermanentFailureContent(layer, {
      centerX,
      shortSide,
      iconBottom: blockTop + iconSize,
      buttonHeight,
      buttonRadius,
      buttonTextSize,
      textResolution,
      onEdit: options.onEdit,
      editLabel: options.editLabel
    });
  }

  const texture = await loadErrorIconTexture();
  if (!layer.destroyed && !icon.destroyed) {
    icon.texture = texture;
    icon.width = iconSize;
    icon.height = iconSize;
  }
}

function buildNetworkFailureContent(
  layer: Container,
  options: {
    centerX: number;
    shortSide: number;
    iconBottom: number;
    buttonHeight: number;
    buttonRadius: number;
    buttonTextSize: number;
    textResolution: number;
    onRetry?: () => void;
  }
): void {
  const textSize = Math.round(options.shortSide * NETWORK_TEXT_RATIO);
  const message = new Text({
    text: '当前网络不稳定，请检查网络后重试',
    resolution: options.textResolution,
    style: {
      fontFamily: 'Microsoft YaHei, PingFang SC, sans-serif',
      fontSize: textSize,
      fontStyle: 'normal',
      fill: NETWORK_TEXT_COLOR,
      align: 'center'
    }
  });
  message.anchor.set(0.5, 0);
  message.roundPixels = true;
  message.position.set(
    options.centerX,
    options.iconBottom + Math.round(options.shortSide * GAP_ICON_NETWORK_TEXT_RATIO)
  );
  layer.addChild(message);

  if (!options.onRetry) return;

  const buttonWidth = Math.round(options.shortSide * RETRY_BUTTON_WIDTH_RATIO);
  const button = buildActionButton({
    width: buttonWidth,
    height: options.buttonHeight,
    radius: options.buttonRadius,
    textSize: options.buttonTextSize,
    textResolution: options.textResolution,
    label: '重新加载',
    onClick: options.onRetry
  });
  button.position.set(
    options.centerX - buttonWidth / 2,
    message.y + textSize + Math.round(options.shortSide * GAP_NETWORK_TEXT_BUTTON_RATIO)
  );
  layer.addChild(button);
}

function buildPermanentFailureContent(
  layer: Container,
  options: {
    centerX: number;
    shortSide: number;
    iconBottom: number;
    buttonHeight: number;
    buttonRadius: number;
    buttonTextSize: number;
    textResolution: number;
    onEdit?: () => void;
    editLabel?: string;
  }
): void {
  const titleSize = Math.round(options.shortSide * TITLE_RATIO);
  const subtitleSize = Math.round(options.shortSide * SUBTITLE_RATIO);
  const title = new Text({
    text: '页面插画生成失败',
    resolution: options.textResolution,
    style: {
      fontFamily: 'Microsoft YaHei, PingFang SC, sans-serif',
      fontSize: titleSize,
      fontWeight: '500',
      fontStyle: 'normal',
      fill: TITLE_COLOR,
      align: 'center'
    }
  });
  title.anchor.set(0.5, 0);
  title.roundPixels = true;
  title.position.set(
    options.centerX,
    options.iconBottom + Math.round(options.shortSide * GAP_ICON_TITLE_RATIO)
  );
  layer.addChild(title);

  const subtitle = new Text({
    text: '本页插画未能成功生成，可重绘调整',
    resolution: options.textResolution,
    style: {
      fontFamily: 'Microsoft YaHei, PingFang SC, sans-serif',
      fontSize: subtitleSize,
      fontStyle: 'normal',
      fill: TITLE_COLOR,
      align: 'center'
    }
  });
  subtitle.alpha = 0.5;
  subtitle.anchor.set(0.5, 0);
  subtitle.roundPixels = true;
  subtitle.position.set(
    options.centerX,
    title.y + titleSize + Math.round(options.shortSide * GAP_TITLE_SUB_RATIO)
  );
  layer.addChild(subtitle);

  if (!options.onEdit) return;

  const buttonWidth = Math.round(options.shortSide * EDIT_BUTTON_WIDTH_RATIO);
  const button = buildActionButton({
    width: buttonWidth,
    height: options.buttonHeight,
    radius: options.buttonRadius,
    textSize: options.buttonTextSize,
    textResolution: options.textResolution,
    label: options.editLabel ?? '前往内容编辑器',
    showArrow: true,
    onClick: options.onEdit
  });
  button.position.set(
    options.centerX - buttonWidth / 2,
    subtitle.y + subtitleSize + Math.round(options.shortSide * GAP_SUBTITLE_BUTTON_RATIO)
  );
  layer.addChild(button);
}

function buildActionButton(options: {
  width: number;
  height: number;
  radius: number;
  textSize: number;
  textResolution: number;
  label: string;
  showArrow?: boolean;
  onClick: () => void;
}): Container {
  const button = new Container();
  button.label = 'page-error-action-button';
  button.eventMode = 'static';
  button.cursor = 'pointer';
  button.hitArea = new Rectangle(0, 0, options.width, options.height);

  const background = new Graphics();
  background.roundRect(0, 0, options.width, options.height, options.radius);
  background.fill({ color: BUTTON_BG, alpha: 1 });
  button.addChild(background);

  const label = new Text({
    text: options.label,
    resolution: options.textResolution,
    style: {
      fontFamily: 'Microsoft YaHei, PingFang SC, sans-serif',
      fontSize: options.textSize,
      fontWeight: 'normal',
      fill: BUTTON_TEXT_COLOR,
      align: 'center'
    }
  });
  label.anchor.set(0, 0.5);
  label.roundPixels = true;

  let arrow: Text | null = null;
  let gap = 0;
  if (options.showArrow) {
    arrow = new Text({
      text: '→',
      resolution: options.textResolution,
      style: {
        fontFamily: 'Microsoft YaHei, PingFang SC, sans-serif',
        fontSize: Math.round(options.height * BUTTON_ICON_HEIGHT_RATIO),
        fontWeight: '600',
        fill: BUTTON_TEXT_COLOR,
        align: 'center'
      }
    });
    arrow.anchor.set(0, 0.5);
    arrow.roundPixels = true;
    gap = Math.round(options.height * BUTTON_TEXT_ICON_GAP_HEIGHT_RATIO);
  }

  const contentWidth = label.width + (arrow?.width ?? 0) + gap;
  const labelX = Math.max(0, (options.width - contentWidth) / 2);
  label.position.set(labelX, options.height / 2);
  button.addChild(label);

  if (arrow) {
    arrow.position.set(labelX + label.width + gap, options.height / 2);
    button.addChild(arrow);
  }

  const stopPointerEvent = (event: FederatedPointerEvent) => {
    event.stopPropagation();
    const nativeEvent = event.nativeEvent as
      | {
          stopImmediatePropagation?: () => void;
          stopPropagation?: () => void;
          preventDefault?: () => void;
        }
      | undefined;
    nativeEvent?.stopImmediatePropagation?.();
    nativeEvent?.stopPropagation?.();
    nativeEvent?.preventDefault?.();
  };

  button.on('pointerdown', stopPointerEvent);
  button.on('pointerup', stopPointerEvent);
  button.on('pointertap', (event: FederatedPointerEvent) => {
    stopPointerEvent(event);
    options.onClick();
  });

  return button;
}
